import { useGSAP } from '@gsap/react';
import { graphql } from 'gatsby';
import gsap from 'gsap';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonLinkType } from '../../../graphql-fragments/ButtonLink';
import { SanityImageType } from '../../../graphql-fragments/SanityImage';
import { BREAKPOINTS } from '../../../styles/breakpoints';
import { ModuleBgColor } from '../../../templates/Page';
import {
  pushCalendlyButtonClickEvent,
  pushWhatsappButtonClickEvent,
  withDataLayer,
} from '../../../tracking/dataLayer';
import { handleCalendlyClick } from '../../../utils/projectUtils';
import { getUrlFromVersatileLink } from '../../../utils/sanity';
import { clsx, replaceNewLinesWithBr } from '../../../utils/utils';
import { CommonModuleProps } from '../../ModulesContent';
import Button from '../Button';
import ButtonLink from '../ButtonLink';
import Form, { FormFieldWithId } from '../Form';
import Image from '../Image';
import ModuleLayout from '../ModuleLayout';
import WhatsappButton from '../WhatsappButton';
import * as styles from './CTAModule.module.scss';

export const CTAModuleFragment = graphql`
  fragment CTAModule on SanityPageCtaModule {
    title {
      ...LocaleStringFragment
    }
    text {
      ...LocaleTextFragment
    }
    addWhatsappButton
    backgroundImage {
      ...SanityImage
    }
  }
`;

export type CTAModuleProps = {
  title: string;
  text: string;
  buttonsTitle?: string;
  button?: ButtonLinkType;
  addWhatsappButton?: boolean;
  backgroundImage?: SanityImageType;
  calendlyLink: string;
  contactForm?: {
    rowFormFieldsWithIds?: Array<FormFieldWithId>;
    formFieldsWithIds: Array<FormFieldWithId>;
  };
  className?: string;
};

export function getModuleBgColor(props: CTAModuleProps): ModuleBgColor {
  /**
   * The purpose of this function is to let other modules know which background color this module has.
   * Knowing this, we can use this function to make decisions about the layout and spacing between modules.
   */
  return 'colored';
}

function CTAModule(props: CTAModuleProps & CommonModuleProps): React.ReactElement {
  const {
    title,
    text,
    backgroundImage,
    buttonsTitle,
    button,
    calendlyLink,
    addWhatsappButton,
    contactForm,
    className,
    previousModuleBgColor,
    moduleId,
  } = props;

  const containerRef = useRef<HTMLElement | null>(null);

  const { i18n, t } = useTranslation();

  useGSAP(
    () => {
      [styles.mobile, styles.desktop].forEach(deviceClassName => {
        gsap
          .timeline({
            scrollTrigger: {
              trigger: `.${styles.contentWrapper}`,
              start: 'top 85%',
            } satisfies ScrollTrigger.Vars,
          })
          // animate text
          .from(`.${deviceClassName} .${styles.buttonWrapper}`, {
            opacity: 0,
            y: 20,
            duration: 0.6,
            stagger: 0.2,
          });
      });

      // image parallax
      if (backgroundImage) {
        gsap.fromTo(
          '.' + styles.backgroundImage,
          {
            y: '-20%',
            transform: 'scale(1.2)',
            ease: 'none',
          },
          {
            y: '0%',
            scrollTrigger: {
              start: 'top bottom',
              end: 'bottom top',
              trigger: '.' + styles.backgroundImageContainer,
              scrub: true,
            } satisfies ScrollTrigger.Vars,
          },
        );
      }
    },
    { scope: containerRef },
  );

  function renderButtonsContainer(classNames: Array<string>) {
    return (
      <div className={clsx(styles.buttonsContainer, ...classNames)}>
        {buttonsTitle && <h3 className={styles.buttonsTitle}>{buttonsTitle}</h3>}
        <div className={styles.buttonsWrapper}>
          <div className={styles.buttonWrapper}>
            {button ? (
              <ButtonLink
                to={getUrlFromVersatileLink(button)}
                onClick={() => {
                  if (button.externalLink?.linkToCalendly) {
                    handleCalendlyClick(calendlyLink);
                    withDataLayer(
                      pushCalendlyButtonClickEvent({ location: 'ctaModule', moduleId }),
                    );
                  }
                }}
                secondary
                withCalendarIcon={!!button.externalLink?.linkToCalendly}
                className={clsx(styles.button)}
              >
                {button.text}
              </ButtonLink>
            ) : (
              <Button
                onClick={() => handleCalendlyClick(calendlyLink)}
                withCalendarIcon
                secondary
                className={clsx(styles.button)}
              >
                {t('schedule_meeting', 'Agendar reunião')}
              </Button>
            )}
          </div>
          {addWhatsappButton && (
            <div className={styles.buttonWrapper}>
              <WhatsappButton
                className={styles.button}
                onClick={() => {
                  withDataLayer(pushWhatsappButtonClickEvent({ location: 'ctaModule', moduleId }));
                }}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <ModuleLayout
      id={moduleId}
      moduleRef={containerRef}
      className={clsx(className, styles.container, backgroundImage && styles.withBackgroundImage)}
      contentClassName={clsx(styles.contentContainer)}
      currentModuleBgColor={getModuleBgColor(props)}
      previousModuleBgColor={previousModuleBgColor}
      childrenClassName={styles.contentWrapper}
    >
      {backgroundImage && (
        <div className={styles.backgroundImageContainer}>
          <Image
            image={backgroundImage}
            className={styles.backgroundImage}
            dimensions={[
              [750, 850],
              [BREAKPOINTS.tabletVertical, 800, 850],
              [BREAKPOINTS.smallLaptop, 2880, 1920],
            ]}
            cover
          />
        </div>
      )}
      <div className={clsx(styles.contentWrapper)}>
        <div className={clsx(styles.textContainer)}>
          <h2 className={clsx(styles.title)}>{title}</h2>
          <p className={clsx(styles.text)}>{replaceNewLinesWithBr(text)}</p>
          {contactForm && (
            <Form
              rowFields={contactForm.rowFormFieldsWithIds}
              fields={contactForm.formFieldsWithIds}
              className={styles.form}
              submitButtonText={t('form.button.send', 'Enviar')}
              withDarkBackground
            />
          )}
          {renderButtonsContainer([styles.mobile, !contactForm ? styles.alwaysVisible : ''])}
        </div>
      </div>
      {backgroundImage && (
        <div className={styles.arcContainer}>
          {contactForm && renderButtonsContainer([styles.desktop])}

          <div className={styles.arc}></div>
        </div>
      )}
    </ModuleLayout>
  );
}

export default CTAModule;
